import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Clock, Award, BookOpen, Play, ChevronRight, Loader2, LogOut } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const translations = {
  en: {
    title: 'My Courses',
    subtitle: 'Track your progress and certifications',
    noCourses: 'No courses yet',
    noCoursesDesc: 'Browse our training catalog and start learning!',
    browseCourses: 'Browse Courses',
    progress: 'Progress',
    timeSpent: 'Time spent',
    hours: 'hours',
    continue: 'Continue',
    completed: 'Completed',
    certificate: 'Certificate',
    viewCertificate: 'View Certificate',
    logout: 'Logout',
    myCertificates: 'My Certificates'
  },
  fr: {
    title: 'Mes Formations',
    subtitle: 'Suivez votre progression et vos certifications',
    noCourses: 'Aucune formation',
    noCoursesDesc: 'Parcourez notre catalogue et commencez à apprendre !',
    browseCourses: 'Voir les Formations',
    progress: 'Progression',
    timeSpent: 'Temps passé',
    hours: 'heures',
    continue: 'Continuer',
    completed: 'Terminé',
    certificate: 'Certificat',
    viewCertificate: 'Voir le Certificat',
    logout: 'Déconnexion',
    myCertificates: 'Mes Certificats'
  },
  pt: {
    title: 'As Minhas Formações',
    subtitle: 'Acompanhe o seu progresso e certificações',
    noCourses: 'Sem formações',
    noCoursesDesc: 'Consulte o nosso catálogo e comece a aprender!',
    browseCourses: 'Ver Formações',
    progress: 'Progresso',
    timeSpent: 'Tempo gasto',
    hours: 'horas',
    continue: 'Continuar',
    completed: 'Concluído',
    certificate: 'Certificado',
    viewCertificate: 'Ver Certificado',
    logout: 'Sair',
    myCertificates: 'Os Meus Certificados'
  },
  de: {
    title: 'Meine Kurse',
    subtitle: 'Verfolgen Sie Ihren Fortschritt und Ihre Zertifikate',
    noCourses: 'Keine Kurse',
    noCoursesDesc: 'Durchsuchen Sie unseren Katalog und beginnen Sie zu lernen!',
    browseCourses: 'Kurse Durchsuchen',
    progress: 'Fortschritt',
    timeSpent: 'Verbrachte Zeit',
    hours: 'Stunden',
    continue: 'Fortsetzen',
    completed: 'Abgeschlossen',
    certificate: 'Zertifikat',
    viewCertificate: 'Zertifikat Anzeigen',
    logout: 'Abmelden',
    myCertificates: 'Meine Zertifikate'
  },
  es: {
    title: 'Mis Cursos',
    subtitle: 'Sigue tu progreso y certificaciones',
    noCourses: 'Sin cursos',
    noCoursesDesc: '¡Explora nuestro catálogo y empieza a aprender!',
    browseCourses: 'Ver Cursos',
    progress: 'Progreso',
    timeSpent: 'Tiempo dedicado',
    hours: 'horas',
    continue: 'Continuar',
    completed: 'Completado',
    certificate: 'Certificado',
    viewCertificate: 'Ver Certificado',
    logout: 'Cerrar sesión',
    myCertificates: 'Mis Certificados'
  }
};

const MyCoursesPage = () => {
  const { language } = useLanguage();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const t = translations[language] || translations.en;

  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [coursesRes, certsRes] = await Promise.all([
        axios.get(`${API}/api/elearning/my-courses`, { withCredentials: true }),
        axios.get(`${API}/api/elearning/my-certificates`, { withCredentials: true })
      ]);
      setCourses(coursesRes.data.courses || []);
      setCertificates(certsRes.data.certificates || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = (item) => {
    return item[`title_${language}`] || item.title_en || item.title_fr || '';
  };

  const calculateProgress = (userCourse) => {
    if (!userCourse || !userCourse.course) return 0;
    const course = userCourse.course;
    const modules = course.modules || [];
    if (modules.length === 0) return 0;

    const modulesProgress = userCourse.modules_progress || {};
    let completedModules = 0;

    modules.forEach(module => {
      const progress = modulesProgress[module.id];
      if (progress && progress.quiz_passed) {
        completedModules++;
      }
    });

    return Math.round((completedModules / modules.length) * 100);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-kamit-cream flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-kamit-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kamit-cream">
      {/* Header */}
      <div className="bg-kamit-dark">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-kamit-gold" />
              <span className="font-heading text-xl text-kamit-beige">IPRBC KAMIT</span>
            </Link>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-kamit-beige font-medium">{user?.name}</p>
                <p className="text-kamit-beige/60 text-sm">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-kamit-beige hover:text-kamit-gold"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-kamit-brown mb-2">{t.title}</h1>
          <p className="text-kamit-brown/60">{t.subtitle}</p>
        </div>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <BookOpen className="h-16 w-16 text-kamit-gold/50 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-kamit-brown mb-2">{t.noCourses}</h3>
            <p className="text-kamit-brown/60 mb-6">{t.noCoursesDesc}</p>
            <Link to="/training">
              <Button className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full">
                {t.browseCourses}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {courses.map((userCourse) => {
              const course = userCourse.course;
              const progress = calculateProgress(userCourse);
              const timeHours = Math.round((userCourse.total_time_spent_seconds || 0) / 3600 * 10) / 10;
              const isCompleted = userCourse.certificate_issued;

              return (
                <div 
                  key={course.id} 
                  className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-video bg-kamit-brown/10 relative overflow-hidden">
                    {course.image_url ? (
                      <img 
                        src={course.image_url} 
                        alt={getTitle(course)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <GraduationCap className="h-16 w-16 text-kamit-brown/30" />
                      </div>
                    )}
                    {isCompleted && (
                      <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        <Award className="h-4 w-4" />
                        {t.completed}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg text-kamit-brown mb-2">
                      {getTitle(course)}
                    </h3>
                    
                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-kamit-brown/60">{t.progress}</span>
                        <span className="text-kamit-gold font-medium">{progress}%</span>
                      </div>
                      <div className="h-2 bg-kamit-cream rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-kamit-gold rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-kamit-brown/60 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {timeHours} {t.hours}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <Link to={`/course/${course.id}`} className="flex-1">
                        <Button className="w-full bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full">
                          <Play className="h-4 w-4 mr-2" />
                          {t.continue}
                        </Button>
                      </Link>
                      {isCompleted && (
                        <Link to={`/certificate/${userCourse.certificate_id}`}>
                          <Button variant="outline" className="border-kamit-gold text-kamit-gold rounded-full">
                            <Award className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Certificates Section */}
        {certificates.length > 0 && (
          <div>
            <h2 className="font-heading text-2xl text-kamit-brown mb-6 flex items-center gap-2">
              <Award className="h-6 w-6 text-kamit-gold" />
              {t.myCertificates}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {certificates.map((cert) => (
                <Link 
                  key={cert.id}
                  to={`/certificate/${cert.id}`}
                  className="bg-gradient-to-r from-kamit-gold/10 to-kamit-green/10 border border-kamit-gold/30 rounded-xl p-4 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-kamit-gold rounded-full flex items-center justify-center">
                      <Award className="h-6 w-6 text-kamit-dark" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-kamit-brown">{cert.course_title}</p>
                      <p className="text-sm text-kamit-brown/60">
                        {new Date(cert.issued_at).toLocaleDateString(language)}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-kamit-gold" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Browse More Courses */}
        <div className="mt-12 text-center">
          <Link to="/training">
            <Button variant="outline" className="border-kamit-brown text-kamit-brown hover:bg-kamit-brown hover:text-kamit-beige rounded-full">
              {t.browseCourses}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MyCoursesPage;
